// Full & Final settlement calculations - a copy of the legacy MVC page
// (Sl-erp-ui Views/HR/EmpFinalSalry.cshtml: CountTotal, GratuityDebitChange, SaveEmpSalary).
// Pure functions so they can be unit-tested (fullFinalCalc.test.js).

// Legacy dropdown values. "Employeer" (sic) is compared literally by spSaveFinalSalary:
// only then is the gratuity debited from the Gratuity CC / DCA budget.
export const GRATUITY_PAY_BY = [
    { value: 'Employeer', label: 'Employer', hint: 'Company pays the gratuity in this settlement (debited from the Gratuity budget)' },
    { value: 'Insurance', label: 'Insurance (LIC)', hint: 'Gratuity is released directly from LIC - it is taken out of the payment amount' },
];

// heads that stay read-only even for daily-wages staff (legacy markup)
const LOCKED_EARNINGS = ['BASIC SALARY', 'PAID LEAVE'];
const LOCKED_DEDUCTIONS = ['LONG TERM ADVANCE', 'SALARY ADVANCE', 'ESI EMPLOYEE CONT.', 'PF EMPLOYEE CONT.'];

export const upper = (s) => (s || '').toString().trim().toUpperCase();
export const isEarningType = (t) => t === 'Earning' || t === 'Benefit' || t === 'OtherBenefit';
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

// Rows of the salary table from the generated settlement. Amounts are kept as strings (input values).
export const buildRows = (d) => {
    const isDW = d?.MonthSalary?.IsDWSalary === 'Yes';
    const toRow = (h, locked) => ({
        name: h.SalaryHead || h.HeadName,
        type: h.HeadType,
        amount: num(h.HeadAmount).toFixed(2),
        pl: h.ApplicableForPL || 'No',
        esi: h.ApplicableForESI || 'No',
        payDays: num(h.PayDays),
        editable: isDW && !locked.includes(upper(h.SalaryHead || h.HeadName)),
        optional: false,
    });
    const heads = d?.lstMonthSalaryHeads || [];
    return {
        earnings: heads.filter(h => isEarningType(h.HeadType)).map(h => toRow(h, LOCKED_EARNINGS)),
        deductions: heads.filter(h => h.HeadType === 'Deduction').map(h => toRow(h, LOCKED_DEDUCTIONS)),
        esiPercent: num((heads.find(h => h.HeadType === 'Deduction') || {}).ESIEmployeeContPercent ?? d?.ESIEmployeeContPercent),
    };
};

// An optional head added through "Add Optional Head" (always editable, like legacy).
// Optional deductions are never PL / ESI applicable (legacy hard-codes 'No').
export const optionalRow = (head, amount) => {
    const earning = isEarningType(head.HeadType);
    return {
        name: head.HeadName,
        type: head.HeadType,
        amount: num(amount).toFixed(2),
        pl: earning ? (head.ApplicableForPL || 'No') : 'No',
        esi: earning ? (head.ApplicableForESI || 'No') : 'No',
        payDays: 0,
        editable: true,
        optional: true,
    };
};

// Legacy CountTotal: recalculates Paid Leave and ESI employee contribution, then the totals.
// ctx = { totalSalaryDays, plDays, plGrossAll, esiPercent }
export const recalcRows = (earnings, deductions, ctx) => {
    const days = num(ctx.totalSalaryDays);
    let e = earnings.map(r => ({ ...r }));
    let dd = deductions.map(r => ({ ...r }));

    // Paid Leave: PL-applicable earnings (not PAID LEAVE itself) / salary days x PL days;
    // when none, the month's PL gross / 26
    const plGross = e.filter(r => r.pl === 'Yes' && upper(r.name) !== 'PAID LEAVE')
        .reduce((s, r) => s + num(r.amount), 0);
    const perDay = plGross === 0 ? num(ctx.plGrossAll) / 26 : (days ? plGross / days : null);
    if (perDay !== null) {
        e = e.map(r => (upper(r.name) === 'PAID LEAVE'
            ? { ...r, amount: Math.round(num(ctx.plDays) * perDay).toFixed(2) } : r));
    }

    // ESI employee contribution: ESI-applicable earnings / salary days x the row's pay days x %
    const esiGross = e.filter(r => r.esi === 'Yes').reduce((s, r) => s + num(r.amount), 0);
    if (esiGross > 0 && days) {
        dd = dd.map(r => (upper(r.name) === 'ESI EMPLOYEE CONT.'
            ? { ...r, amount: Math.round((num(ctx.esiPercent) / 100) * num(r.payDays) * (esiGross / days)).toFixed(2) }
            : r));
    }

    const gross = e.reduce((s, r) => s + num(r.amount), 0);
    const deduction = dd.reduce((s, r) => s + num(r.amount), 0);
    return { earnings: e, deductions: dd, gross, deduction };
};

// Net due / recovery after the other earnings (bonus basic, leave encashment, gratuity), as legacy.
export const finalTotals = ({ gross, deduction, otherEarnings }) => {
    const fe = num(gross) + num(otherEarnings);
    const fd = num(deduction);
    return {
        finalGross: Math.round(fe),
        finalDeduction: Math.round(fd),
        finalNet: fe > fd ? Math.round(fe - fd) : 0,
        recovery: fe < fd ? Math.round(fd - fe) : 0,
    };
};

// Payment amount (legacy GratuityDebitChange): when gratuity is released by insurance it is
// taken out of the net; when there is nothing to pay the payment is 0.
export const paymentAmount = ({ finalNet, gratuity, gratuityPayBy }) => {
    const net = num(finalNet);
    if (num(gratuity) > 0 && gratuityPayBy === 'Insurance') {
        return net > 0 ? Math.round(net - num(gratuity)) : 0;
    }
    return Math.round(net);
};

// Month net sent on save: gross - deduction, or 0 when deductions are higher.
export const monthNet = (gross, deduction) =>
    (num(gross) >= num(deduction) ? +(num(gross) - num(deduction)).toFixed(2) : 0);

// Legacy SaveEmpSalary checks. Returns a list of messages (empty = valid).
export const validateSettlement = ({ earnings, deductions, gratuity, gratuityPayBy }) => {
    const errors = [];
    const all = [...earnings, ...deductions];
    if (all.length === 0) errors.push('Invalid salary: no salary heads');
    const names = all.map(r => upper(r.name));
    if (names.some((n, i) => names.indexOf(n) !== i)) errors.push('Duplicate salary heads');
    if (all.some(r => r.amount === '' || r.amount === '.' || isNaN(parseFloat(r.amount)) || parseFloat(r.amount) < 0)) {
        errors.push('Enter a valid amount for every salary head');
    }
    if (num(gratuity) > 0 && !gratuityPayBy) errors.push('Select who pays the gratuity (Gratuity Pay By)');
    return errors;
};
